import * as React from 'react';
import { useRecoilValue } from 'recoil';
import { useIntl } from 'react-intl';
import {
  ALARM_ARROW_COLOR,
  EVENT_ARROW_COLOR,
  IS_ALARM_LAYER,
  IS_ECO_DRIVE_LAYER,
  IS_EVENT_LAYER,
  IS_RIDE_LAYER,
} from '../constants';
import {
  IRide,
  IRideMapArrowMarkers,
  IRideMapMarkerTooltips,
  IRidePlayerSpeedLimitsSettings,
  TRideActiveLayer,
} from '../types';
import {
  activeRideNumberAtom,
  enableArrowsAtom,
  enableSpeedlimitsAtom,
  isRideActiveAtom,
  selectedRidesAtom,
  showAllRidesAtom,
  speedlimitSettingsAtom,
} from '../RidePlayerState';
import {
  createArrowMarkers,
  hideAllTooltips,
  updateAllArrowMarkersColor,
  updateAllArrowMarkersColorByEcoScore,
  updateAllArrowMarkersColorBySpeedlimits,
  updateArrowMarkersWithNewPosition,
} from '../RidePlayerMarkerService';

interface IUseArrowMarker {
  arrowMarkers: IRideMapArrowMarkers;
  showAllArrowMarkers: () => void;
  toggleAllArrowMarkerTooltips: (toggleValue: boolean) => void;
  removeAllArrowMarkers: () => void;
  hideAllArrowMarkers: () => void;
  hideArrowMarkersForRide: (rideNumber: number) => void;
  showArrowMarkersForRide: (rideNumber: number) => void;
}

/**
 * useArrowMarker custom hook
 * @param {google.maps.Map} map
 * @param {IRide[]} rides
 * @param {number} activeRide
 * @param {IRidePosition | null | undefined} actualRidePosition
 * @returns {IUseArrowMarker}
 */
const useArrowMarker = (
  map: google.maps.Map,
  rides: IRide[],
  activeLayer: TRideActiveLayer,
  isLastRidePlayer?: boolean
): IUseArrowMarker => {
  const intl = useIntl();
  const arrowMarkers = React.useRef<IRideMapArrowMarkers>({});
  const arrowMarkerTooltips = React.useRef<IRideMapMarkerTooltips>({ 1: [] });
  const enableArrows = useRecoilValue<boolean>(enableArrowsAtom);
  const enableSpeedlimits = useRecoilValue<boolean>(enableSpeedlimitsAtom);
  const speedlimitSettings = useRecoilValue<IRidePlayerSpeedLimitsSettings>(speedlimitSettingsAtom);
  const selectedRides = useRecoilValue<number[]>(selectedRidesAtom);
  const activeRide = useRecoilValue<number>(activeRideNumberAtom);
  const isRideActive = useRecoilValue<number | false>(isRideActiveAtom);
  const showAllRides = useRecoilValue<boolean>(showAllRidesAtom);

  /**
   * Toggle marker tooltip handler
   * @param {number} rideNumber
   * @param {number{ positionIndex
   * @returns {void}
   */
  const handleToggleTooltip = React.useCallback(
    (rideNumber: number, positionIndex: number): void => {
      if (
        arrowMarkerTooltips.current[rideNumber] &&
        arrowMarkerTooltips.current[rideNumber][positionIndex]
      ) {
        const markerTooltip = arrowMarkerTooltips.current[rideNumber][positionIndex];
        hideAllTooltips(arrowMarkerTooltips, positionIndex);
        if (markerTooltip) {
          markerTooltip.setMap(markerTooltip.getMap() ? null : map);
        }
      }
    },
    [map]
  );

  /**
   * Show all arrow markers
   * @returns void
   */
  const showAllArrowMarkers = React.useCallback(() => {
    Object.keys(arrowMarkers.current).forEach((rideNumber: string) => {
      if (selectedRides.includes(Number(rideNumber))) {
        Object.keys(arrowMarkers.current[Number(rideNumber)]).forEach((positionIndex: string) => {
          const marker = arrowMarkers.current[Number(rideNumber)][positionIndex];
          if (marker && !marker.getMap() && selectedRides.includes(Number(rideNumber))) {
            marker.setMap(map);
          }
        });
      }
    });
  }, [map, selectedRides]);

  /**
   * Hide all arrow markers
   * @returns void
   */
  const hideAllArrowMarkers = React.useCallback(() => {
    Object.keys(arrowMarkers.current).forEach((rideNumber: string) => {
      Object.keys(arrowMarkers.current[Number(rideNumber)]).forEach((positionIndex: string) => {
        const marker = arrowMarkers.current[Number(rideNumber)][positionIndex];
        if (marker && marker.getMap()) {
          marker.setMap(null);
        }
      });
    });
  }, []);

  /**
   * Show arrow marker for specified ride
   * @param {number} rideNumber - Ride number
   * @returns {void}
   */
  const showArrowMarkersForRide = React.useCallback(
    (rideNumber: number): void => {
      if (arrowMarkers.current[rideNumber] && selectedRides.includes(rideNumber)) {
        Object.keys(arrowMarkers.current[rideNumber]).forEach((positionIndex: string) => {
          const marker = arrowMarkers.current[rideNumber][positionIndex];
          if (marker && !marker.getMap()) {
            marker.setMap(map);
          }
        });
      }
    },
    [map, selectedRides]
  );

  /**
   * Hide arrow marker for specified ride
   * @param {number} rideNumber
   * @returns {void}
   */
  const hideArrowMarkersForRide = (rideNumber: number): void => {
    if (arrowMarkers.current[rideNumber] && selectedRides.includes(rideNumber)) {
      Object.keys(arrowMarkers.current[rideNumber]).forEach((positionIndex: string) => {
        const marker = arrowMarkers.current[rideNumber][positionIndex];
        if (marker && marker.getMap()) {
          marker.setMap(null);
        }
      });
    }
  };

  /**
   * Empty arrow markers and tooltips
   */
  const removeAllArrowMarkers = () => {
    arrowMarkers.current = {};
    arrowMarkerTooltips.current = {};
  };

  /**
   * Show/hide all arrow marker tooltips
   * @param {boolean} toggleValue
   * @returns void
   */
  const toggleAllArrowMarkerTooltips = React.useCallback(
    (toggleValue: boolean): void => {
      Object.keys(arrowMarkerTooltips.current).forEach((rideNumber: string) => {
        Object.keys(arrowMarkerTooltips.current[Number(rideNumber)]).forEach(
          (positionIndex: string) => {
            const markerTooltip =
              arrowMarkerTooltips.current[Number(rideNumber)][Number(positionIndex)];
            if (markerTooltip && toggleValue && !markerTooltip.getMap()) {
              markerTooltip.setMap(map);
            }
            if (markerTooltip && !toggleValue && markerTooltip.getMap()) {
              markerTooltip.setMap(null);
            }
          }
        );
      });
    },
    [map]
  );

  // Handle show/hide arrow markers by selected rides and active ride
  React.useEffect(() => {
    Object.keys(arrowMarkers.current).forEach((rideNumber: string) => {
      Object.keys(arrowMarkers.current[Number(rideNumber)]).forEach((positionIndex: string) => {
        const marker = arrowMarkers.current[Number(rideNumber)][positionIndex];
        if (!selectedRides.includes(Number(rideNumber)) && marker.getMap()) {
          marker.setMap(null);
        }
        if (
          selectedRides.includes(Number(rideNumber)) &&
          !marker.getMap() &&
          enableArrows &&
          isRideActive === Number(rideNumber)
        ) {
          marker.setMap(map);
        }
      });
    });
  }, [enableArrows, isRideActive, map, selectedRides]);

  // Create arrow markers on mount
  React.useEffect(() => {
    // arrowMarkers.current = RidePlayerMarkerService.createArrowMarkers(
    //   enableArrows && showAllRides ? map : undefined,
    //   rides,
    //   10,
    //   enableSpeedlimits,
    //   speedlimitSettings,
    //   activeLayer
    // );
    createArrowMarkers(
      enableArrows && showAllRides ? map : undefined,
      arrowMarkers.current,
      arrowMarkerTooltips.current,
      rides,
      10,
      enableSpeedlimits,
      speedlimitSettings,
      activeLayer,
      intl,
      handleToggleTooltip
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Create arrow marker for new position when last ride player is active
  React.useEffect(() => {
    if (activeRide && selectedRides.includes(activeRide) && isLastRidePlayer) {
      updateArrowMarkersWithNewPosition(
        enableArrows ? map : undefined,
        arrowMarkers.current,
        arrowMarkerTooltips.current,
        activeRide,
        rides[activeRide - 1],
        enableSpeedlimits,
        speedlimitSettings,
        activeLayer,
        intl,
        handleToggleTooltip,
        activeRide
      );
    }
  }, [
    activeLayer,
    activeRide,
    enableArrows,
    enableSpeedlimits,
    handleToggleTooltip,
    intl,
    isLastRidePlayer,
    map,
    rides,
    selectedRides,
    speedlimitSettings,
  ]);

  // If enableArrow is changed, toggle arrow markers
  React.useEffect(() => {
    if (enableArrows) {
      if (isRideActive) {
        showArrowMarkersForRide(isRideActive);
      } else {
        showAllArrowMarkers();
      }
    } else {
      hideAllArrowMarkers();
    }
  }, [
    enableArrows,
    hideAllArrowMarkers,
    showAllArrowMarkers,
    isRideActive,
    showArrowMarkersForRide,
  ]);

  React.useEffect(() => {
    if (activeLayer === IS_RIDE_LAYER) {
      updateAllArrowMarkersColorBySpeedlimits(
        arrowMarkers.current,
        enableSpeedlimits,
        speedlimitSettings
      );
    }
    if (activeLayer === IS_ALARM_LAYER) {
      updateAllArrowMarkersColor(arrowMarkers.current, ALARM_ARROW_COLOR);
    }
    if (activeLayer === IS_ECO_DRIVE_LAYER) {
      updateAllArrowMarkersColorByEcoScore(arrowMarkers.current);
    }
    if (activeLayer === IS_EVENT_LAYER) {
      updateAllArrowMarkersColor(arrowMarkers.current, EVENT_ARROW_COLOR);
    }
  }, [enableSpeedlimits, activeLayer, speedlimitSettings]);

  const data: IUseArrowMarker = {
    arrowMarkers: arrowMarkers.current,
    showAllArrowMarkers,
    toggleAllArrowMarkerTooltips,
    removeAllArrowMarkers,
    hideAllArrowMarkers,
    showArrowMarkersForRide,
    hideArrowMarkersForRide,
  };

  return data;
};

export default useArrowMarker;
